/**
 * Swappable source for "what's the next milestone target?".
 *
 * On-chain this is `Season.next_milestone_mcap_lamports` (the floor a
 * posted mcap must clear to trigger a crossing) plus `peak_mcap_lamports`
 * (the high-water mark — also the floor in the monotonic check).
 *
 * The bridge hook `useFlywheelOnChainBridge` polls the active Season PDA
 * (tracked via the `solblox.season_id_hex` localStorage key the ChainTab
 * dev panel writes) and calls `setOnchain(...)`. Until a Season is tracked,
 * `sample()` returns a stub progression so the panel still demonstrates
 * the bar filling. `sample()` is sync — the cache is the only state.
 */
import { LAMPORTS_PER_SOL } from "@solana/web3.js";

export interface MilestoneTargets {
  /** High-water mark of mcap posted to chain (lamports). */
  peakLamports: number;
  /** Threshold the next posted mcap must clear (lamports). */
  nextThresholdLamports: number;
  /** Bps multiplier the keeper applies on each crossing (e.g. 15000 = 1.5×). */
  multiplierBps: number;
  /** Where the numbers came from. */
  source: "stub" | "onchain";
}

export interface MilestoneTargetSource {
  /**
   * Compute targets given the current observed mcap. On-chain reads ignore
   * the input; the stub fallback uses it to synthesize a believable shape.
   */
  sample(currentMcapLamports: number): MilestoneTargets;
}

const STUB_MULTIPLIER_BPS = 15_000;

function stubFallback(currentMcapLamports: number): MilestoneTargets {
  const floor = Math.max(currentMcapLamports, 50 * LAMPORTS_PER_SOL);
  return {
    peakLamports: Math.floor(floor * 0.8),
    nextThresholdLamports: Math.floor(floor * 1.5),
    multiplierBps: STUB_MULTIPLIER_BPS,
    source: "stub",
  };
}

/**
 * Holds an on-chain Season snapshot when one is available and falls back
 * to a synthesized progression otherwise. The bridge hook owns the
 * setOnchain / clear lifecycle; consumers only call sample().
 */
class SwappableMilestoneTargetSource implements MilestoneTargetSource {
  private cache: MilestoneTargets | null = null;

  sample(currentMcapLamports: number): MilestoneTargets {
    return this.cache ?? stubFallback(currentMcapLamports);
  }

  setOnchain(s: {
    peakLamports: number;
    nextThresholdLamports: number;
    multiplierBps: number;
  }): void {
    this.cache = { ...s, source: "onchain" };
  }

  clear(): void {
    this.cache = null;
  }
}

export const milestoneTargets = new SwappableMilestoneTargetSource();
